<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Administrative Locations</b-breadcrumb-item>
        </b-breadcrumb>

        <div class="card">
            <div class="bg-dark card-header text-white">Adlocs</div>
            <div class="card-body">
                <!-- The collapsable filter options -->
                <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
                    <template v-slot:header>Filter</template>
                    <template v-slot:body>
                        <div class="form-row">
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Number</label>
                                <input
                                        class="form-control"
                                        placeholder="Adloc Number"
                                        v-model="filterOptions.id"
                                />
                            </div>
                        </div>
                    </template>
                    <template v-slot:footer>
                        <button class="btn btn-primary" type="submit">Apply</button>
                    </template>
                </AppFormCardCollapsable>

                <div class="d-print-none dropdown mb-3 text-right">
                    <button aria-expanded="false" aria-haspopup="true" class="btn btn-secondary dropdown-toggle"
                            data-toggle="dropdown" style="width: 100px" type="button">
                        <span>Actions</span>
                    </button>
                    <div class="dropdown-menu">
                        <RouterLink :to="{name: 'AddAdministrativeLocation'}"
                                    class="btn btn-outline-light dropdown-item text-left"
                                    title="Add new Administrative Location">
                            <i class="fa fa-plus"></i>Add
                        </RouterLink>
                    </div>
                </div>

                <BTable
                        :busy="isLoading"
                        :fields="tableFields"
                        :items="administrativeLocations"
                        :responsive="true"
                        primary-key="id"
                        show-empty
                        striped
                        table-class="table-align-middle">

                    <template v-slot:cell(null)="row">
                        <div class="btn-group">
                            <router-link :to="{name:'AdministrativeLocationDetails',params:{id:row.item.id}}"
                                         class="btn btn-sm btn-warning d-print-none"
                                         data-toggle="tooltip"
                                         title="Edit general code details">
                                <i class="fa fa-pencil-alt"></i>Edit
                            </router-link>
                            <button @click="onDeleteClick(row.item)" class="btn btn-danger btn-sm d-print-none"
                                    type="button"><i class="fa fa-trash"></i>Delete
                            </button>
                        </div>
                    </template>

                    <template v-slot:cell(id)="row">
                        {{row.item.id | adlocNumber}}
                    </template>

                    <!-- Footer Pagination -->
                    <template v-slot:custom-foot>
                        <b-tr>
                            <b-td :colspan="tableFields.length">
                                <AppTablePagination
                                        :page.sync="filterOptions.page"
                                        :pageSize.sync="filterOptions.pageSize"
                                        :totalCount.sync="totalAdministrativeLocations"
                                        v-on:update:page="loadData()"
                                        v-on:update:pageSize="loadData()"
                                ></AppTablePagination>
                            </b-td>
                        </b-tr>
                    </template>

                </BTable>
            </div>

            <AppModalConfirm :visible="deletingRow" @close="deletingRow=null" @confirm="onDeleteConfirmed(deletingRow)">
                <template v-slot:header>Confirm</template>
                <p v-if="deletingRow">Are you sure you want to delete '{{deletingRow.id}}'?</p>
            </AppModalConfirm>

        </div>

    </div>
</template>

<script>
    import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable";
    import AppTablePagination from "@/components/shared/AppTablePagination";
    import {ToastType} from "@/services/models/shared/ToastType";
    import AdministrativeLocationService from "../../services/AdministrativeLocationService";
    import AppModalConfirm from "@/components/shared/AppModalConfirm";

    export default {
        components: {
            AppModalConfirm,
            AppFormCardCollapsable,
            AppTablePagination,
        },
        data() {
            return {
                administrativeLocations: [],
                totalAdministrativeLocations: 0,
                deletingRow: null,
                filterOptions: {
                    page: 1,
                    pageSize: 10,
                    id: null
                },
                isLoading: false,
                tableFields: [
                    {
                        key: 'null',
                        label: '',
                        thStyle: 'width:175px'
                    }, {
                        key: 'id', // TODO: Display template / filter on this to display properly
                        label: 'Number',
                        thStyle: 'width:150px'
                    },
                    {
                        key: 'description',
                        label: 'Description',
                    }
                ]
            }
        },
        filters: {
            // The display template for a given adloc number
            adlocNumber: function (value) {
                if (!value) return;

                value = value.toString();

                if (value.length > 2) {
                    return `${value.substring(0, 2)}-${value.substring(2, value.length)}`
                } else {
                    return value;
                }
            }
        },
        methods: {
            /**
             * @description Loads the data from the API with the filter options bound to the component
             */
            loadData() {
                const vm = this;
                vm.isLoading = true;

                AdministrativeLocationService.getAdministrativeLocationList(vm.filterOptions)
                    .then(response => {
                        vm.administrativeLocations = response.data;
                        vm.totalAdministrativeLocations = response.totalCount;
                    })
                    .catch(() => {
                        vm.redirectToError()
                    })
                    .finally(() => {
                        vm.isLoading = false;
                    })
            },

            /**
             * @description Resets the page to the first page and reloads the data
             */
            onFilterSubmit() {
                this.filterOptions.page = 1;
                this.loadData();
            },

            /**
             * @description Opens up a modal to confirm deletion of the adloc
             */
            onDeleteClick(administrativeLocation) {
                this.deletingRow = administrativeLocation;
            },

            /**
             * @description When the user clicks 'Confirm' - the selected adloc will be deleted and the modal will be closed.
             */
            onDeleteConfirmed(deletingRow) {
                const vm = this;

                AdministrativeLocationService.deleteAdministrativeLocation(deletingRow.id)
                    .then(() => {
                        vm.alert(ToastType.Success, `The adloc '${deletingRow.id}' was successfully deleted`);

                        // Reload the data on the page
                        vm.loadData();
                    })
                    .catch(error => {
                        vm.alert(ToastType.Danger, error.response.statusText)
                    }).finally(() =>
                    vm.deletingRow = null);
            }
        },
        created() {
            this.loadData();
        }
    }
</script>

